import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { BrowserWindow, BrowserWindowSymbol, EditorFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import { name } from './symbols'
import { IRootScrollApi, ScrollState, ViewerManagerScrollApiEditorConfig } from './types'
import { getFullId } from '@wix/thunderbolt-commons'
import _ from 'lodash'
import { ILightbox, LightboxSymbol } from 'feature-lightbox'
import { IEditorAnimations, EditorAnimationsSym } from 'feature-animations'

export const RootScrollApi = withDependencies(
	[named(EditorFeatureConfigSymbol, name), BrowserWindowSymbol, LightboxSymbol, EditorAnimationsSym],
	(
		config: ViewerManagerScrollApiEditorConfig,
		window: NonNullable<BrowserWindow>,
		popups: ILightbox,
		animationManager: IEditorAnimations
	): IRootScrollApi => {
		const getWindowScroll = (): ScrollState => ({
			y: window.scrollY,
			x: window.scrollX,
		})

		const getElementScroll = (element: HTMLElement): ScrollState => ({
			y: element.scrollTop,
			x: element.scrollLeft,
		})

		const getRootScrollForComp = (compId: string) =>
			!config.isResponsive && config.popupDescendants[getFullId(compId)]
				? getElementScroll(document.getElementById('POPUPS_ROOT')!)
				: getWindowScroll()

		const setScroll = (x: number, y: number) => {
			if (popups.isOpen()) {
				const popupRootDOMNode = window.document.getElementById('POPUPS_ROOT')
				if (popupRootDOMNode) {
					popupRootDOMNode.scrollLeft = x
					popupRootDOMNode.scrollTop = y
				}
			} else {
				window.scrollTo(x, y)
			}
		}

		const getScroll = (ignorePopups: boolean = false) => {
			if (popups.isOpen() && !ignorePopups) {
				const popupRootDOMNode = window.document.getElementById('POPUPS_ROOT')

				return {
					x: _.get(popupRootDOMNode, 'scrollLeft', 0),
					y: _.get(popupRootDOMNode, 'scrollTop', 0),
				}
			}

			return {
				x: _.isFinite(window.pageXOffset) ? window.pageXOffset : window.scrollX,
				y: _.isFinite(window.pageYOffset) ? window.pageYOffset : window.scrollY,
			}
		}

		const setScrollAndScale = (
			x: number,
			y: number,
			scale: number,
			duration: number,
			originLeft: number,
			callback: Function
		) => {
			const animationContainers = ['#site-root', '#popups-wrapper']
			const animations = animationManager.getPreviewAnimationsService()
			const callbacks = { onComplete: callback }
			const sequence = animations.sequence({ callbacks })
			const left = !_.isUndefined(originLeft) ? originLeft.toString() : '50%'
			sequence
				.add(animations.animate('BaseNone', animationContainers, 0, 0, { transformOrigin: `${left} 0` }), 0)
				.add(
					animations.animate('BaseScale', animationContainers, duration, 0, {
						to: { scale },
						clearProps: scale === 1 ? 'transform, transform-origin' : '',
					}),
					0
				)
				.add(animations.animate('BaseScroll', window, duration, 0, { x, y }), 0)

			return sequence.get()
		}

		const animateScroll = (x: number, y: number, duration = 0, callback = _.noop) => {
			const animations = animationManager.getPreviewAnimationsService()
			const callbacks = { onComplete: callback }
			const sequence = animations.sequence({ callbacks })
			sequence.add(animations.animate('BaseScroll', window, duration, 0, { x, y }), 0)
			return sequence.get()
		}

		return {
			getRootScrollForComp,
			setScroll,
			getScroll,
			setScrollAndScale,
			animateScroll,
		}
	}
)
